/**
 * Modern Form Submission Component
 * 
 * A modernized form submission component with enhanced features:
 * - Manual draft save functionality
 * - Multiple submissions per form/patient support
 * - Improved UI/UX with better user feedback
 * - Component-based field rendering
 * - Real-time validation
 * - Submission history management
 * - Backward compatibility with existing forms
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Container, Row, Col, Card, Button, Alert, Spinner, Badge, Form, Table } from "react-bootstrap";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import { FaSave, FaPaperPlane, FaUndo, FaEdit, FaEye } from 'react-icons/fa';

// Components
import Dashboardheader from "./dashboardheader";
import Sidebar from "./sidebar";
import FormField from "./FormFields/FormField";
import FormProgress from "./FormFields/FormProgress";
import TopbarNavigation from "./TopbarNavigation";

import FormSubmissionHistory from "./FormSubmissionHistory";
import FormTemplateManager from "./FormTemplateManager";

// Services and utilities
import dynamicFormsService from "../../services/dynamicFormsService";
import rbacService from "../../services/rbacService";
import parentService from "../../services/parentService";
import { getPatientDataAction } from "../../Action/admin.action";
import propertise from "../../config/properties";
import { evaluateFormula, parseFormulaFields } from "../../utils/formulaBuilder";
import { getCurrentUser, userHasRole } from '../../utils/authUtils';
import {
  addRecentForm,
  addRecentSearch,
  trackFormAccess,
  addNavigationHistory,
  addPatientToContext,
  savePatientFormState,
  setCurrentPatient
} from '../../redux/slices/navigationSlice';

// Styles
import "../componentCss/webform.css";
import "../componentCss/sidebar.css";
import "./FormFields/ModernFormSubmission.css";

// Helper function to generate unique field key
const getFieldKey = (field, index) => {
  const baseId = field.field_id || field.id;
  return `${baseId}_${index}`;
};

// Helper to get field value with fallback for backward compatibility
const getFieldValue = (field, index, formValues) => {
  const compositeKey = getFieldKey(field, index);
  const simpleKey = field.field_id || field.id;
  return formValues[compositeKey] ?? formValues[simpleKey] ?? '';
};

// Helper to convert form values to submission data array format
const convertFormValuesToSubmissionData = (formData) => {
  const fieldMap = new Map();

  Object.entries(formData).forEach(([key, value]) => {
    const parts = key.split('_');
    const lastPart = parts[parts.length - 1];

    let fieldId;
    let hasIndex = false;

    // Check if key has an index suffix (e.g., fieldId_0)
    if (parts.length >= 2 && !isNaN(lastPart) && lastPart === parseInt(lastPart).toString()) {
      fieldId = parts.slice(0, -1).join('_');
      hasIndex = true;
    } else {
      fieldId = key;
    }

    // Prefer composite keys (with index) over simple keys to avoid duplicates
    if (!fieldMap.has(fieldId) || hasIndex) {
      fieldMap.set(fieldId, value);
    }
  });

  return Array.from(fieldMap.entries()).map(([field_id, value]) => ({
    field_id,
    value
  }));
};

const ModernFormSubmission = () => {
  const { formId, id, patientId, status } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Get current user for permission checks
  const currentUser = getCurrentUser();

  // Check if user is parent role - parents see data-only view (no form fields)
  const isParentRole = userHasRole('parent');

  // Get navigation state to check if this is a consent form
  const location = useLocation();
  const isConsentForm = location.state?.isConsentForm || false;

  // Redux state
  const navigationState = useSelector(state => state.navigation);
  const recentFormsFromRedux = navigationState?.recentForms || [];
  const multiPatientContext = navigationState?.multiPatientContext;
  const currentPatientId = multiPatientContext?.currentPatientId;
  const activePatients = multiPatientContext?.activePatients || [];

  // Form state
  const [form, setForm] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  
  // Permission state
  const [hasEditPermission, setHasEditPermission] = useState(true);
  const [isCheckingPermission, setIsCheckingPermission] = useState(true);
  const [isReadOnlyMode, setIsReadOnlyMode] = useState(false);

  // Navigation state
  const [availableForms, setAvailableForms] = useState([]);
  const [formFolders, setFormFolders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFolder, setSelectedFolder] = useState('all');

  // Draft state
  const [currentDraft, setCurrentDraft] = useState(null);
  const [isLoadingDraft, setIsLoadingDraft] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);


  // Patient state
  const [patientDetails, setPatientDetails] = useState({
    emrNumber: '',
    mrnNumber: `MRN${Math.floor(Math.random() * 900000) + 100000}`
  });
  const [isLoadingPatientData, setIsLoadingPatientData] = useState(false);
  const [patientLoadError, setPatientLoadError] = useState(null);
  const [isSwitchingPatient, setIsSwitchingPatient] = useState(false);

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submissionHistoryKey, setSubmissionHistoryKey] = useState(0);

  // Refs to prevent duplicate API calls
  const hasLoadedPatientData = useRef(false);
  const hasLoadedDraft = useRef(false);
  const currentPatientIdRef = useRef(patientId);
  const currentFormIdRef = useRef(formId);

  // 🚀 PERFORMANCE OPTIMIZATION: Add flag to prevent duplicate submission history calls
  const isRefreshingSubmissionHistory = useRef(false);

  // Computed values
  const formProgress = useMemo(() => {
    if (!form?.fields) return 0;

    const totalFields = form.fields.filter(field => field.required).length;
    if (totalFields === 0) return 100;

    const completedFields = form.fields.filter(field => {
      if (!field.required) return true;
      const value = formValues[field.field_id];
      return value !== undefined && value !== null && value !== '';
    }).length;

    return Math.round((completedFields / totalFields) * 100);
  }, [form, formValues]);

  const hasUnsavedChanges = isDirty && !isSavingDraft;

  // ============================================================================
  // PERMISSION CHECKING
  // ============================================================================
  
  const checkFormPermission = useCallback(async () => {
    if (!currentUser || !formId) {
      setHasEditPermission(false);
      setIsCheckingPermission(false);
      return;
    }

    try {
      setIsCheckingPermission(true);
      
      // Check if status param indicates view mode
      if (status === 'view') {
        setIsReadOnlyMode(true);
        setHasEditPermission(false);
        setIsCheckingPermission(false);
        return;
      }

      // Early return for nurses/doctors (privileged users)
      const userRoles = currentUser.roles || [currentUser.role];
      if (userRoles.includes('nurse') || userRoles.includes('doctor') ||
          userRoles.includes('physician') || userRoles.includes('super_admin')) {
        setHasEditPermission(true);
        setIsReadOnlyMode(false);
        setIsCheckingPermission(false);
        console.log('📋 Form permission check: Privileged user - full access granted');
        return;
      }

      // Allow parents to fill consent forms (they have consent.view and consent.fill permissions)
      if (isParentRole && isConsentForm) {
        setHasEditPermission(true);
        setIsReadOnlyMode(false);
        setIsCheckingPermission(false);
        console.log('📋 Form permission check: Parent filling consent form - access granted');
        return;
      }

      // Get user permissions (uses cached value if available, only calls API if cache is stale)
      const permissions = await rbacService.getUserComprehensivePermissions(currentUser.id);
      
      console.log('📋 Full permissions response:', permissions);
      
      // Method 1: Use pre-calculated form_permissions array (most accurate)
      const formPermissions = permissions.data?.form_permissions || [];
      const formPerm = formPermissions.find(fp => fp.form_id === formId);
      
      console.log('📋 Form permissions array:', {
        formPermissions,
        formId,
        foundPerm: formPerm
      });
      
      if (formPerm) {
        const canEdit = formPerm.can_edit || formPerm.can_submit || formPerm.can_view || false;
        setHasEditPermission(canEdit);
        setIsReadOnlyMode(!canEdit);

        console.log('📋 Form permission check (from form_permissions):', {
          formId,
          userId: currentUser.id,
          canEdit,
          formPerm
        });
        setIsCheckingPermission(false);
        return;
      }
      
      // Method 2: Check effective_permissions dict as fallback
      const effectivePermissions = permissions.data?.effective_permissions || {};
      
      console.log('📋 Checking effective permissions:', {
        formId,
        availablePermissions: Object.keys(effectivePermissions),
        checkingFor: [`forms.${formId}.edit`, `forms.${formId}.view`, `forms.*.edit`, `forms.*.view`, 'form.submit']
      });
      
      // Check for form edit permissions
      const canEdit = effectivePermissions['forms.*.edit'] ||
                     effectivePermissions['forms.*.create'] ||
                     effectivePermissions['forms.*.view'] ||
                     effectivePermissions['form.submit'] ||
                     effectivePermissions[`forms.${formId}.edit`] ||
                     effectivePermissions[`forms.${formId}.create`] ||
                     effectivePermissions[`forms.${formId}.view`];
      
      setHasEditPermission(canEdit);
      setIsReadOnlyMode(!canEdit);
      
      console.log('📋 Form permission check (from effective_permissions):', {
        formId,
        userId: currentUser.id,
        canEdit,
        effectivePermissions
      });
    } catch (error) {
      console.error('Error checking form permission:', error);
      setHasEditPermission(false);
      setIsReadOnlyMode(true);
    } finally {
      setIsCheckingPermission(false);
    }
  }, [currentUser, formId, status, isParentRole, isConsentForm]);

  // Check permissions when form loads
  useEffect(() => {
    checkFormPermission();
  }, [checkFormPermission]);

  // ============================================================================
  // DATA LOADING
  // ============================================================================

  const loadNavigationData = useCallback(async () => {
    try {
      // 🚀 PERFORMANCE OPTIMIZATION: Use batch loading instead of multiple separate calls
      console.log('🚀 Loading navigation data using batch method...');
      const batchResponse = await dynamicFormsService.batchLoadNavigationData();

      // Set forms
      if (batchResponse.forms.success) {
        const forms = batchResponse.forms.data || [];
        console.log('🚀 Loaded forms for navigation:', forms.slice(0, 3)); // Log first 3 forms for debugging
        setAvailableForms(forms);
      }

      // Set folders
      if (batchResponse.folders.success) {
        setFormFolders(batchResponse.folders.data || []);
      }

      // Set departments (if needed)
      if (batchResponse.departments.success) {
        console.log('🚀 Loaded departments:', batchResponse.departments.data?.length || 0);
      }

      // Templates removed to eliminate unnecessary API call

      console.log('🚀 Navigation data batch loaded successfully');
    } catch (err) {
      console.error('Error loading navigation data:', err);
    }
  }, []);

  // 🚀 PERFORMANCE OPTIMIZATION: Add debouncing to prevent duplicate submission history calls
  const loadSubmissionHistory = useCallback(() => {
    // Prevent duplicate calls
    if (isRefreshingSubmissionHistory.current) {
      console.log('🚀 Submission history refresh already in progress, skipping duplicate call');
      return;
    }

    isRefreshingSubmissionHistory.current = true;

    // Force refresh of FormSubmissionHistory component by changing its key
    // Add debouncing to prevent rapid successive calls
    const timeoutId = setTimeout(() => {
      setSubmissionHistoryKey(prev => prev + 1);
      // Reset flag after a delay to allow future refreshes
      setTimeout(() => {
        isRefreshingSubmissionHistory.current = false;
      }, 500);
    }, 100); // 100ms debounce

    return () => clearTimeout(timeoutId);
  }, []);

  // Handle draft loaded from submission history
  const handleDraftLoaded = useCallback((draftData, draftMetadata) => {
    try {
      console.log('📝 Draft loaded from submission history:', draftData, draftMetadata);

      // Ensure draftData is an object
      const safeDraftData = draftData && typeof draftData === 'object' ? draftData : {};

      // Set the draft data as form values
      setFormValues(safeDraftData);
      setIsDirty(true);
      setValidationErrors({});

      // Set the form to draft mode using real draft metadata
      if (draftMetadata) {
        setCurrentDraft({
          submission_id: draftMetadata.submission_id,
          draft_id: draftMetadata.draft_id,
          form_id: draftMetadata.form_id || formId,
          data: Object.entries(safeDraftData).map(([field_id, value]) => ({
            field_id,
            value
          })),
          draft_saved_at: draftMetadata.draft_saved_at || new Date().toISOString(),
          status: draftMetadata.status || 'draft'
        });
      } else {
        // Fallback to old behavior if no metadata provided
        setCurrentDraft({
          submission_id: 'loaded-draft',
          form_id: formId,
          data: Object.entries(safeDraftData).map(([field_id, value]) => ({
            field_id,
            value
          })),
          draft_saved_at: new Date().toISOString(),
          status: 'draft'
        });
      }

      toast.success('Draft data loaded into form successfully');
    } catch (error) {
      console.error('Error loading draft data:', error);
      toast.error('Failed to load draft data into form');
    }
  }, [formId]);

  // Handle template loaded from submission history
  const handleTemplateLoaded = useCallback((templateData) => {
    try {
      console.log('📋 Template loaded from submission history:', templateData);

      // Ensure templateData is an object
      const safeTemplateData = templateData && typeof templateData === 'object' ? templateData : {};

      // Set the template data as form values
      setFormValues(safeTemplateData);
      setIsDirty(true);
      setValidationErrors({});

      // Clear any existing draft since we're using template data
      setCurrentDraft(null);

      toast.success('Template data loaded into form successfully');
    } catch (error) {
      console.error('Error loading template data:', error);
      toast.error('Failed to load template data into form');
    }
  }, []);

  const loadCurrentDraft = useCallback(async (targetPatientId = patientId, targetFormId = formId) => {
    try {
      console.log('🔄 loadCurrentDraft called with:', { targetPatientId, targetFormId, isLoadingDraft });

      // Check if we've already loaded draft for this patient/form combination
      if (hasLoadedDraft.current &&
        currentPatientIdRef.current === targetPatientId &&
        currentFormIdRef.current === targetFormId) {
        console.log('✅ Draft already loaded for this patient/form combination, skipping');
        return;
      }

      console.log('🚀 Starting draft load for patient:', targetPatientId, 'form:', targetFormId);
      setIsLoadingDraft(true);

      const draftResponse = await dynamicFormsService.getCurrentDraft(targetFormId, targetPatientId);
      console.log('📥 Draft response received:', { success: draftResponse.success, hasData: !!draftResponse.data });

      if (draftResponse.success && draftResponse.data) {
        const draft = draftResponse.data;
        setCurrentDraft(draft);

        // Validate draft data against current form fields
        const draftFormValues = {};
        const validFieldIds = new Set(form?.fields?.map(f => f.field_id) || []);

        // Add null check for draft.data before calling forEach
        if (draft.data && Array.isArray(draft.data)) {
          draft.data.forEach(fieldData => {
            // Only load values for fields that exist in current form
            if (validFieldIds.has(fieldData.field_id)) {
              draftFormValues[fieldData.field_id] = fieldData.value;
            }
          });
        }

        setFormValues(draftFormValues);
        setLastSavedAt(new Date(draft.draft_saved_at));
        setIsDirty(false);

        // Clear any previous validation errors
        setValidationErrors({});

        // Mark as loaded for this patient/form combination
        hasLoadedDraft.current = true;
        currentPatientIdRef.current = targetPatientId;
        currentFormIdRef.current = targetFormId;

        console.log('✅ Draft loaded successfully for patient:', patientDetails.full_name || 'patient');
        toast.success(`Draft loaded for ${patientDetails.full_name || 'patient'}`, {
          duration: 2000,
          icon: '📝'
        });
      } else {
        console.log('⚠️ No draft found for patient:', targetPatientId, 'form:', targetFormId);

        // Check if patient has saved form state in Redux
        const savedState = multiPatientContext?.activePatients
          ?.find(p => p.id === targetPatientId)
          ?.formState?.[targetFormId];

        if (savedState && savedState.values) {
          setFormValues(savedState.values);
          setIsDirty(savedState.isDirty || false);
          toast.success('Previous form state restored', { duration: 2000 });
        }

        // Mark as loaded for this patient/form combination
        hasLoadedDraft.current = true;
        currentPatientIdRef.current = targetPatientId;
        currentFormIdRef.current = targetFormId;
      }
    } catch (err) {
      console.error('❌ Error loading draft:', err);
      // Only show error for actual failures, not missing drafts
      if (err.message && !err.message.includes('not found')) {
        toast.error(`Failed to load draft: ${err.message}`);
      }
    } finally {
      setIsLoadingDraft(false);
      console.log('🏁 Draft loading completed');
    }
  }, [formId, patientId]);

  const loadFormData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load form configuration
      // Use parent service for consent forms, otherwise use dynamic forms service
      let formResponse;
      if (isParentRole && isConsentForm) {
        console.log('📋 Loading consent form via parent service');
        formResponse = await parentService.getConsentFormById(formId);
      } else {
        formResponse = await dynamicFormsService.getFormById(formId);
      }

      if (!formResponse.success) {
        throw new Error(formResponse.message || 'Failed to load form');
      }

      const formData = formResponse.data;
      setForm(formData);

      // Load existing draft for this patient/form
      if (patientId) {
        await loadCurrentDraft();
      }

    } catch (err) {
      setError(err.message || 'Failed to load form');
      toast.error(err.message || 'Failed to load form');
    } finally {
      setLoading(false);
    }
  }, [formId, patientId, isParentRole, isConsentForm]);

  const loadPatientData = useCallback(async (targetPatientId = patientId, retryAttempt = 0) => {
    const maxRetries = 2;

    console.log('🔄 loadPatientData called with:', { targetPatientId, retryAttempt, isLoadingPatientData: isLoadingPatientData });

    // Prevent multiple simultaneous calls
    if (isLoadingPatientData) {
      console.log('⚠️ Patient data already loading, skipping duplicate call');
      return;
    }

    // Check if we've already loaded data for this patient
    if (hasLoadedPatientData.current && currentPatientIdRef.current === targetPatientId) {
      console.log('✅ Patient data already loaded for this patient, skipping');
      return;
    }

    console.log('🚀 Starting patient data load for:', targetPatientId);
    setIsLoadingPatientData(true);
    setPatientLoadError(null);

    try {
      const response = await getPatientDataAction({
        patientId: targetPatientId,
        formId: propertise.patientAdmit
      });

      console.log('📥 Patient data response received:', { success: response.success, hasData: !!response.data });

      if (response.success && response.data) {
        // The response.data from handleApiResponse contains the actual patient data
        // The actual patient data is nested in response.data.data
        const data = response.data.data || response.data;

        // Handle both new API structure and legacy structure
        const updatedPatientDetails = {
          emrNumber: data.emrNumber || data.current_mrn || '',
          mrnNumber: data.mrnNumber || data.current_mrn || patientDetails.mrnNumber,
          full_name: data.full_name || data.demographics?.full_name || data.name || 'Unknown Patient',
          age: data.age || data.demographics?.age,
          gender: data.gender || data.demographics?.gender,
          department: data.department || data.care_status?.current_services?.[0] || '',
          status: data.status || data.care_status?.status || 'active'
        };

        setPatientDetails(updatedPatientDetails);

        // Add patient to multi-patient context after loading data
        const patientContextData = {
          id: targetPatientId,
          mrn: updatedPatientDetails.mrnNumber,
          mrn_number: updatedPatientDetails.mrnNumber, // For compatibility
          full_name: updatedPatientDetails.full_name,
          age: updatedPatientDetails.age,
          gender: updatedPatientDetails.gender,
          department: updatedPatientDetails.department,
          status: updatedPatientDetails.status,
          // Add additional fields from new API structure
          emirates_id: data.emirates_id,
          phone_primary: data.demographics?.phone_primary,
          email: data.demographics?.email,
          nationality: data.demographics?.nationality,
          // Add demographics for compatibility
          demographics: data.demographics,
          care_status: data.care_status
        };

        dispatch(addPatientToContext(patientContextData));

        // Mark as loaded for this patient
        hasLoadedPatientData.current = true;
        currentPatientIdRef.current = targetPatientId;

        console.log('✅ Patient data loaded successfully for:', updatedPatientDetails.full_name);
        toast.success(`Patient data loaded: ${updatedPatientDetails.full_name}`, { duration: 2000 });
      } else {
        console.warn('⚠️ No patient data received for patient:', targetPatientId);

        // Create minimal patient data for context
        const minimalPatientData = {
          id: targetPatientId,
          mrn: `MRN${targetPatientId}`,
          mrn_number: `MRN${targetPatientId}`,
          full_name: 'Patient',
          status: 'active'
        };

        setPatientDetails({
          emrNumber: '',
          mrnNumber: minimalPatientData.mrn,
          full_name: minimalPatientData.full_name
        });

        dispatch(addPatientToContext(minimalPatientData));

        // Mark as loaded for this patient
        hasLoadedPatientData.current = true;
        currentPatientIdRef.current = targetPatientId;

        toast.error('Patient data not found, using minimal information');
      }
    } catch (err) {
      console.error('❌ Error loading patient data:', err);
      setPatientLoadError(err.message || 'Failed to load patient data');

      if (retryAttempt < maxRetries) {
        console.log(`🔄 Retrying patient data load (attempt ${retryAttempt + 1}/${maxRetries})`);
        setTimeout(() => {
          loadPatientData(targetPatientId, retryAttempt + 1);
        }, 1000 * (retryAttempt + 1)); // Exponential backoff
      } else {
        toast.error(`Failed to load patient data: ${err.message}`);
      }
    } finally {
      setIsLoadingPatientData(false);
      console.log('🏁 Patient data loading completed');
    }
  }, [patientId, dispatch]);

  const initializeFormData = useCallback(async () => {
    console.log('🔄 initializeFormData called for patient:', patientId, 'form:', formId);
    console.log('URL parameters:', { formId, id, patientId });

    // Reset state when switching patients
    setFormValues({});
    setValidationErrors({});
    setCurrentDraft(null);
    setIsDirty(false);
    setLastSavedAt(null);
    setPatientLoadError(null);

    await loadFormData();
    loadNavigationData(); // Load forms and folders for navigation

    // Load patient data first if we have a patientId
    if (patientId && patientId !== 'undefined') {
      console.log('🚀 Loading patient data for:', patientId);
      await loadPatientData(patientId);
      loadSubmissionHistory();

      // Check for template data in localStorage
      const templateKey = `template_${formId}_${patientId}`;
      const templateData = localStorage.getItem(templateKey);
      if (templateData) {
        try {
          const parsed = JSON.parse(templateData);
          const cacheAge = Date.now() - parsed.timestamp;

          // Use template data if it's less than 1 hour old
          if (cacheAge < 60 * 60 * 1000 && parsed.data) {
            console.log('📋 Loading template data from localStorage');

            // Ensure the data is in the correct format
            const templateData = typeof parsed.data === 'object' ? parsed.data : {};
            setFormValues(templateData);
            setIsDirty(true);
            toast.success('Template data loaded successfully');

            // Clear the template data from localStorage after loading
            localStorage.removeItem(templateKey);
          } else {
            // Clear expired template data
            localStorage.removeItem(templateKey);
          }
        } catch (error) {
          console.error('Error parsing template data:', error);
          localStorage.removeItem(templateKey);
        }
      }

      // Check for draft data in localStorage
      const draftKey = `draft_${formId}_${patientId}`;
      const draftData = localStorage.getItem(draftKey);
      if (draftData) {
        try {
          const parsed = JSON.parse(draftData);
          const cacheAge = Date.now() - parsed.timestamp;

          // Use draft data if it's less than 1 hour old
          if (cacheAge < 60 * 60 * 1000 && parsed.data) {
            console.log('📝 Loading draft data from localStorage');

            // Ensure the data is in the correct format
            const draftData = typeof parsed.data === 'object' ? parsed.data : {};
            setFormValues(draftData);
            setIsDirty(true);
            toast.success('Draft data loaded successfully');

            // Clear the draft data from localStorage after loading
            localStorage.removeItem(draftKey);
          } else {
            // Clear expired draft data
            localStorage.removeItem(draftKey);
          }
        } catch (error) {
          console.error('Error parsing draft data:', error);
          localStorage.removeItem(draftKey);
        }
      }
    }
  }, [formId, patientId, id]);

  useEffect(() => {
    console.log('🔄 useEffect [initializeFormData] triggered');
    initializeFormData();
  }, [initializeFormData]);

  // Process form values when form is loaded to filter out invalid fields
  useEffect(() => {
    if (form && formValues && Object.keys(formValues).length > 0) {
      console.log('🔄 Processing form values to filter invalid fields');

      const validFieldIds = new Set(form.fields?.map(f => f.field_id) || []);
      const filteredValues = {};
      let hasInvalidFields = false;

      Object.entries(formValues).forEach(([fieldId, value]) => {
        if (validFieldIds.has(fieldId)) {
          filteredValues[fieldId] = value;
        } else {
          hasInvalidFields = true;
        }
      });

      if (hasInvalidFields) {
        console.log('⚠️ Removed invalid fields from form values');
        setFormValues(filteredValues);
      }
    }
  }, [form]); // Only depend on form, not formValues to avoid loops

  // Reload draft when form is loaded and patient changes
  useEffect(() => {
    console.log('🔄 useEffect [form, patientId, isLoadingDraft] triggered:', {
      hasForm: !!form,
      patientId,
      isLoadingDraft
    });

    if (form && patientId && patientId !== 'undefined' && !isLoadingDraft) {
      console.log('🚀 Loading draft for patient:', patientId, 'form:', form.id);
      loadCurrentDraft();
    }
  }, [form, patientId, isLoadingDraft]);

  // Track form access when component loads
  useEffect(() => {
    console.log('🔄 useEffect [form, dispatch, formId, patientDetails] triggered');
    if (form) {
      dispatch(trackFormAccess({ formId: form.id || formId }));
      dispatch(addRecentForm(form));
      dispatch(addNavigationHistory({
        path: window.location.pathname,
        label: `Form: ${form.name}${patientDetails?.full_name ? ` - ${patientDetails.full_name}` : ''}`
      }));
    }
  }, [form, dispatch, formId, patientDetails]);

  // Reset refs when patientId or formId changes
  useEffect(() => {
    console.log('🔄 useEffect [patientId, formId] triggered:', { patientId, formId });
    if (currentPatientIdRef.current !== patientId || currentFormIdRef.current !== formId) {
      console.log('🔄 Patient or form changed, resetting refs');
      hasLoadedPatientData.current = false;
      hasLoadedDraft.current = false;
      currentPatientIdRef.current = patientId;
      currentFormIdRef.current = formId;

      // 🚀 PERFORMANCE OPTIMIZATION: Clear form-specific cache when switching
      if (currentFormIdRef.current !== formId) {
        console.log('🚀 Form changed, clearing form-specific cache');
        dynamicFormsService.clearCache();
        dynamicFormsService.constructor.clearGlobalCache(); // Clear global cache too
      }
    }
  }, [patientId, formId]);

  // ============================================================================
  // NAVIGATION DATA LOADING
  // ============================================================================

  // 🚀 PERFORMANCE OPTIMIZATION: Clear expired cache every 5 minutes
  useEffect(() => {
    const cacheCleanupInterval = setInterval(() => {
      console.log('🚀 Clearing expired cache entries...');
      dynamicFormsService.clearExpiredCache();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(cacheCleanupInterval);
  }, []);

  // Formula recalculation function
  const recalculateFormulas = useCallback((currentValues, changedFieldId = null) => {
    if (!form || !form.fields) return currentValues;

    // Find all formula fields with their indices
    const formulaFieldsWithIndices = form.fields
      .map((field, index) => ({ field, index }))
      .filter(({ field }) => field.field_type === 'formula' || field.fieldtype === 'formula');

    if (formulaFieldsWithIndices.length === 0) return currentValues;

    let updatedValues = { ...currentValues };

    // Recalculate each formula field
    formulaFieldsWithIndices.forEach(({ field: formulaField, index: formulaIndex }) => {
      const expression = formulaField.formula_expression;
      if (!expression) return;

      const referencedFieldIds = formulaField.referenced_field_ids ||
        parseFormulaFields(expression);

      // Build field values map for evaluation
      const fieldValues = {};
      let hasAllValues = true;

      referencedFieldIds.forEach(refId => {
        // Find the referenced field AND its index in form.fields
        // Try multiple field ID resolution strategies for flexibility
        const refFieldIndex = form.fields.findIndex(f => {
          const isNumberField = f.field_type === 'number' || f.fieldtype === 'number';
          if (!isNumberField) return false;

          // Try multiple field ID resolution strategies
          const fieldId = f.field_id || f.id;
          const fieldName = f.field_name || f.fieldName;
          const labelDerivedId = f.label?.toLowerCase().replace(/[^a-z0-9]/g, '_');

          // Match against any of these identifiers
          return fieldId === refId ||
                 fieldName === refId ||
                 labelDerivedId === refId ||
                 f.id === refId;
        });

        if (refFieldIndex !== -1) {
          const refField = form.fields[refFieldIndex];

          // Get value using the referenced field's actual index (composite key)
          const value = getFieldValue(refField, refFieldIndex, updatedValues);

          if (value !== null && value !== undefined && value !== '') {
            const numValue = parseFloat(value);
            if (!isNaN(numValue)) {
              fieldValues[refId] = numValue;
            } else {
              hasAllValues = false;
            }
          } else {
            hasAllValues = false;
          }
        } else {
          // Referenced field not found
          hasAllValues = false;
          console.warn(`Referenced field "${refId}" not found in form.fields`);
        }
      });

      // Calculate if all dependencies have values
      if (hasAllValues && Object.keys(fieldValues).length > 0) {
        const result = evaluateFormula(expression, fieldValues);

        if (result.success) {
          const decimalPlaces = formulaField.decimal_places ?? 2;
          const calculatedValue = parseFloat(result.value.toFixed(decimalPlaces));

          // Store using composite key (formula field's index)
          const formulaFieldKey = getFieldKey(formulaField, formulaIndex);
          updatedValues[formulaFieldKey] = calculatedValue;

          // Debug logging
          console.log('Formula calculated:', {
            formulaField: formulaField.field_name || formulaField.label,
            formulaIndex,
            expression,
            fieldValues,
            result: calculatedValue,
            storedAt: formulaFieldKey
          });
        } else {
          // Clear formula value on calculation error
          const formulaFieldKey = getFieldKey(formulaField, formulaIndex);
          updatedValues[formulaFieldKey] = '';
          console.warn('Formula calculation failed:', {
            formulaField: formulaField.field_name || formulaField.label,
            expression,
            error: result.error,
            clearedAt: formulaFieldKey
          });
        }
      } else {
        // Clear formula value when dependencies are incomplete
        const formulaFieldKey = getFieldKey(formulaField, formulaIndex);
        updatedValues[formulaFieldKey] = '';

        // Only log if not all values are present (common during initial render)
        if (Object.keys(fieldValues).length > 0) {
          console.log('Formula waiting for values:', {
            formulaField: formulaField.field_name || formulaField.label,
            expression,
            receivedValues: fieldValues,
            missingCount: referencedFieldIds.length - Object.keys(fieldValues).length
          });
        }
      }
    });

    return updatedValues;
  }, [form]);

  // Initialize formula values when form loads or formValues change
  useEffect(() => {
    if (form && form.fields && Object.keys(formValues).length > 0) {
      const formulaFields = form.fields.filter(f =>
        f.field_type === 'formula' || f.fieldtype === 'formula'
      );

      // Only recalculate if there are formula fields and we have some values
      if (formulaFields.length > 0) {
        setFormValues(prev => recalculateFormulas(prev));
      }
    }
  }, [form, recalculateFormulas]);

  // ============================================================================
  // NAVIGATION HANDLERS
  // ============================================================================

  const handleTopbarFolderSelect = (folderId) => {
    setSelectedFolder(folderId);
  };

  const handleTopbarFormSelect = (selectedForm) => {
    // Debug logging to help identify the issue
    console.log('handleTopbarFormSelect called with:', {
      selectedForm,
      form_id: selectedForm?.form_id,
      id: selectedForm?.id,
      _id: selectedForm?._id,
      patientId,
      currentFormId: formId
    });

    // Check if there are unsaved changes
    if (isDirty) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to switch forms?')) {
        return;
      }
    }

    // Save current form state if there are changes
    if (isDirty && patientId) {
      dispatch(savePatientFormState({
        patientId,
        formId: form?.id || formId,
        formState: {
          values: formValues,
          isDirty,
          lastModified: new Date().toISOString()
        }
      }));
    }

    // Track form access and navigation
    // The form_id field contains the MongoDB ObjectId as a string (from FormSummaryResponse schema)
    const formIdToTrack = selectedForm.form_id || selectedForm.id || selectedForm._id;

    // Validate form ID
    if (!formIdToTrack) {
      console.error('No valid form ID found in selectedForm:', selectedForm);
      toast.error('Invalid form selected. Please try again.');
      return;
    }

    // Ensure formId is a string and not an object
    let validatedFormId = formIdToTrack;
    if (typeof validatedFormId === 'object') {
      if (validatedFormId._id) {
        validatedFormId = validatedFormId._id;
      } else if (validatedFormId.$oid) {
        validatedFormId = validatedFormId.$oid;
      } else if (validatedFormId.id) {
        validatedFormId = validatedFormId.id;
      } else {
        console.error('Invalid form ID object:', validatedFormId);
        toast.error('Invalid form ID format. Please try again.');
        return;
      }
    }

    // Ensure formId is a string
    validatedFormId = validatedFormId.toString().trim();
    if (!validatedFormId || validatedFormId === 'undefined' || validatedFormId === 'null') {
      console.error('Invalid form ID after validation:', validatedFormId);
      toast.error('Invalid form ID. Please try again.');
      return;
    }

    dispatch(trackFormAccess({ formId: validatedFormId }));
    dispatch(addRecentForm(selectedForm));
    dispatch(addNavigationHistory({
      path: window.location.pathname,
      label: `Switched to form: ${selectedForm.name || selectedForm.formName}`
    }));

    // Navigate to the new form
    const newFormId = validatedFormId;

    // Handle patientId properly - if it's undefined or 'undefined', navigate without patientId
    if (patientId && patientId !== 'undefined') {
      console.log('Navigating to:', `/form-submission/${newFormId}/new/${patientId}`);
      navigate(`/form-submission/${newFormId}/new/${patientId}`);
    } else {
      console.log('Navigating to:', `/form-submission/${newFormId}/new`);
      navigate(`/form-submission/${newFormId}/new`);
    }
  };

  const handleTopbarSearchChange = (searchValue) => {
    setSearchTerm(searchValue);

    if (searchValue && searchValue.length >= 2) {
      dispatch(addRecentSearch(searchValue));
    }
  };

  const handlePatientChange = async (newPatient, isNewPatient = true) => {
    // Prevent switching if already in progress
    if (isSwitchingPatient) {
      return;
    }

    // Smart confirmation logic - only show for significant changes
    const hasSignificantChanges = isDirty && Object.keys(formValues).length > 0;
    if (hasSignificantChanges) {
      const confirmed = window.confirm(
        `You have unsaved changes that will be lost.\n\n` +
        `Current Patient: ${patientDetails.full_name}\n` +
        `Switching to: ${newPatient.full_name}\n\n` +
        `Do you want to save your changes first?`
      );

      if (!confirmed) {
        return;
      }

      // Auto-save draft before switching
      try {
        await saveDraft();
        toast.success('Draft saved before switching patients');
      } catch (err) {
        console.error('Failed to save draft before switch:', err);
        // Continue with switch even if draft save fails
      }
    }

    setIsSwitchingPatient(true);

    try {
      // Save current form state if there are changes
      if (isDirty && patientId) {
        dispatch(savePatientFormState({
          patientId,
          formId: form?.id || formId,
          formState: {
            values: formValues,
            isDirty,
            lastModified: new Date().toISOString(),
            progress: formProgress
          }
        }));
      }

      // Set the new patient as current patient in Redux
      dispatch(setCurrentPatient(newPatient.id));

      dispatch(addNavigationHistory({
        path: window.location.pathname,
        label: `Switched to patient: ${newPatient.full_name}`
      }));

      // Only navigate if this is a new patient (from dropdown)
      if (isNewPatient) {
        // Show loading toast
        const loadingToast = toast.loading(`Switching to ${newPatient.full_name}...`);

        try {
          // Navigate to new patient
          const newUrl = `/form-submission/${formId}/new/${newPatient.id}`;
          console.log('Navigating to:', newUrl);
          navigate(newUrl, { replace: true });

          // Wait for navigation to complete
          setTimeout(() => {
            toast.dismiss(loadingToast);
            toast.success(`Successfully switched to ${newPatient.full_name}`, {
              icon: '👤',
              duration: 3000
            });
          }, 500);
        } catch (error) {
          console.error('Navigation error:', error);
          toast.dismiss(loadingToast);
          toast.error('Failed to switch patient');
        }
      } else {
        // For existing patients in tabs, just show success message
        toast.success(`Switched to ${newPatient.full_name}`, {
          icon: '👤',
          duration: 2000
        });
      }
    } catch (error) {
      console.error('Error during patient switch:', error);
      toast.error(`Failed to switch to ${newPatient.full_name}: ${error.message}`);
    } finally {
      setIsSwitchingPatient(false);
    }
  };



  // ============================================================================
  // FORM HANDLING
  // ============================================================================

  const handleFieldChange = useCallback((field, index, value) => {
    const compositeKey = getFieldKey(field, index);
    const originalFieldId = field.field_id || field.id;

    setFormValues(prev => {
      const updated = { ...prev, [compositeKey]: value };
      // Recalculate formulas using original field ID
      return recalculateFormulas(updated, originalFieldId);
    });
    setIsDirty(true);

    // Clear validation error (validation uses original field ID)
    if (validationErrors[originalFieldId]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[originalFieldId];
        return newErrors;
      });
    }
  }, [validationErrors, recalculateFormulas]);

  const handlePatientDetailsChange = useCallback((e) => {
    const { name, value } = e.target;
    setPatientDetails(prev => ({ ...prev, [name]: value }));
    setIsDirty(true);
  }, []);

  const handleTemplateApply = useCallback((templateData) => {
    // Apply template data to form
    if (templateData && typeof templateData === 'object') {
      setFormValues(prev => ({
        ...prev,
        ...templateData
      }));
      setIsDirty(true);
      toast.success('Template applied successfully');
    }
  }, []);

  const validateForm = () => {
    if (!form?.fields) return true;

    const errors = {};
    let isValid = true;

    form.fields.forEach((field, index) => {
      const value = getFieldValue(field, index, formValues);

      if (field.required) {
        if (value === undefined || value === null || value === '') {
          errors[field.field_id] = `${field.label} is required`;
          isValid = false;
        }
      }

      // Additional field-specific validation
      if (value) {
        // Email validation
        if (field.field_type === 'email' && value) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            errors[field.field_id] = 'Please enter a valid email address';
            isValid = false;
          }
        }

        // Number validation
        if (field.field_type === 'number' && value) {
          const numValue = parseFloat(value);
          if (isNaN(numValue)) {
            errors[field.field_id] = 'Please enter a valid number';
            isValid = false;
          }
          if (field.min_value !== null && numValue < field.min_value) {
            errors[field.field_id] = `Value must be at least ${field.min_value}`;
            isValid = false;
          }
          if (field.max_value !== null && numValue > field.max_value) {
            errors[field.field_id] = `Value must be at most ${field.max_value}`;
            isValid = false;
          }
        }

        // Text length validation
        if ((field.field_type === 'text' || field.field_type === 'textarea') && value) {
          if (field.min_length && value.length < field.min_length) {
            errors[field.field_id] = `Minimum length is ${field.min_length} characters`;
            isValid = false;
          }
          if (field.max_length && value.length > field.max_length) {
            errors[field.field_id] = `Maximum length is ${field.max_length} characters`;
            isValid = false;
          }
        }
      }
    });

    setValidationErrors(errors);
    return isValid;
  };

  // ============================================================================
  // DRAFT OPERATIONS
  // ============================================================================

  const saveDraft = async () => {
    try {
      setIsSavingDraft(true);

      // Prepare draft data
      const draftData = {
        form_id: formId,
        data: convertFormValuesToSubmissionData(formValues),
        submitter_info: {
          patient_id: patientId,
          emr_number: patientDetails.emrNumber,
          mrn_number: patientDetails.mrnNumber,
          encounter_id: id || null
        }
      };

      let response;
      if (currentDraft) {
        // Update existing draft
        response = await dynamicFormsService.updateDraft(
          formId,
          currentDraft.submission_id,
          draftData
        );
      } else {
        // Create new draft
        response = await dynamicFormsService.saveDraft(formId, draftData);
      }

      if (response.success) {
        setCurrentDraft(response.data);
        setLastSavedAt(new Date());
        setIsDirty(false);
        toast.success('Draft saved successfully');
      } else {
        throw new Error(response.message || 'Failed to save draft');
      }

    } catch (err) {
      toast.error(err.message || 'Failed to save draft');
    } finally {
      setIsSavingDraft(false);
    }
  };

  // ============================================================================
  // FORM SUBMISSION
  // ============================================================================

  const submitForm = async () => {
    if (!validateForm()) {
      toast.error('Please fix validation errors before submitting');
      return;
    }

    try {
      setIsSubmitting(true);

      const submissionData = {
        form_id: formId,
        data: convertFormValuesToSubmissionData(formValues),
        submitter_info: {
          patient_id: patientId,
          emr_number: patientDetails.emrNumber,
          mrn_number: patientDetails.mrnNumber,
          encounter_id: id || null
        }
      };

      let response;
      if (currentDraft) {
        // Submit existing draft - use draft_id if available, otherwise use submission_id
        const draftIdToUse = currentDraft.draft_id || currentDraft.submission_id;
        if (!draftIdToUse) {
          throw new Error('No draft ID available for submission');
        }
        response = await dynamicFormsService.submitDraft(formId, draftIdToUse);
      } else if (isConsentForm && isParentRole) {
        // Parent submitting consent form - use parent-specific endpoint
        response = await parentService.submitConsentForm(formId, patientId, submissionData.data);
      } else {
        // Direct submission for regular forms
        response = await dynamicFormsService.submitForm(formId, submissionData);
      }

      if (response.success) {
        toast.success('Form submitted successfully');

        // Reset form state completely
        setFormValues({});
        setValidationErrors({});
        setIsDirty(false);
        setCurrentDraft(null);
        setLastSavedAt(null);

        // Reload submission history
        if (patientId) {
          loadSubmissionHistory();
        }

        // Navigate or stay based on form configuration
        // You can add navigation logic here based on requirements

      } else {
        throw new Error(response.message || 'Failed to submit form');
      }

    } catch (err) {
      toast.error(err.message || 'Failed to submit form');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    if (hasUnsavedChanges) {
      if (!window.confirm('Are you sure you want to reset the form? Unsaved changes will be lost.')) {
        return;
      }
    }

    setFormValues({});
    setValidationErrors({});
    setCurrentDraft(null);
    setIsDirty(false);
    setLastSavedAt(null);
    toast.success('Form reset successfully');
  };

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  // Helper function to format field values for display (used in parent data-only view)
  const formatFieldValue = (field, value) => {
    if (value === null || value === undefined || value === '') {
      return <span className="text-muted">Not provided</span>;
    }

    // Handle different field types
    const fieldType = field.field_type || field.fieldtype || field.type;
    switch (fieldType) {
      case 'checkbox':
        return value === true || value === 'true' || value === 1 ? 'Yes' : 'No';
      case 'date':
        try {
          return new Date(value).toLocaleDateString();
        } catch {
          return String(value);
        }
      case 'datetime':
      case 'datetime-local':
        try {
          return new Date(value).toLocaleString();
        } catch {
          return String(value);
        }
      case 'select':
      case 'dropdown':
      case 'radio':
        // Find option label if available
        const option = field.options?.find(o => o.value === value || o.id === value);
        return option?.label || option?.text || String(value);
      case 'multiselect':
        // Handle array of values
        if (Array.isArray(value)) {
          return value.map(v => {
            const opt = field.options?.find(o => o.value === v || o.id === v);
            return opt?.label || opt?.text || String(v);
          }).join(', ');
        }
        return String(value);
      case 'file':
        return value?.name || value?.filename || 'File attached';
      case 'number':
      case 'formula':
        return typeof value === 'number' ? value.toLocaleString() : String(value);
      default:
        return String(value);
    }
  };

  // Render submission data as a clean table (for parent role in read-only mode)
  const renderSubmissionDataOnly = () => {
    if (!form?.fields || !formValues) return null;

    // Filter out non-data fields (headers, paragraphs, etc.)
    const dataFields = form.fields.filter(field => {
      const fieldType = field.field_type || field.fieldtype || field.type;
      return !['header', 'paragraph', 'markdown', 'divider', 'section'].includes(fieldType);
    });

    return (
      <Card className="shadow-sm mb-4">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">{form.name}</h5>
            <Badge bg="info">
              <FaEye className="me-1" />
              View Only
            </Badge>
          </div>
          {form.description && (
            <small className="text-muted">{form.description}</small>
          )}
        </Card.Header>
        <Card.Body>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th style={{ width: '40%' }}>Field</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              {dataFields.map((field, index) => {
                const value = getFieldValue(field, index, formValues);
                return (
                  <tr key={field.field_id || field.id || index}>
                    <td>
                      <strong>{field.label}</strong>
                      {field.required && <span className="text-danger ms-1">*</span>}
                    </td>
                    <td>{formatFieldValue(field, value)}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
  };

  const renderFormActions = () => {
    // Don't show action buttons in read-only mode
    if (isReadOnlyMode) {
      return (
        <Alert variant="info" className="mb-3">
          <div className="d-flex align-items-center">
            <FaEye className="me-2" size={20} />
            <div>
              <strong>Read-Only Mode</strong>
              <p className="mb-0 mt-1">
                You are viewing this form in read-only mode. You do not have permission to edit or submit this form.
                {!hasEditPermission && ' Contact your administrator to request edit access.'}
              </p>
            </div>
          </div>
        </Alert>
      );
    }

    return (
      <Card className="shadow-sm">
        <Card.Body>
          <Row className="align-items-center">
            <Col md={6}>
              <div className="d-flex align-items-center">
                <FormProgress progress={formProgress} />
                {isSwitchingPatient && (
                  <Badge bg="info" className="ms-2">
                    <Spinner animation="border" size="sm" className="me-1" />
                    Switching...
                  </Badge>
                )}
              </div>
              {lastSavedAt && (
                <small className="text-muted d-block mt-1">
                  <FaSave className="me-1" />
                  Last saved: {lastSavedAt.toLocaleTimeString()}
                </small>
              )}
              {currentDraft && (
                <small className="text-info d-block mt-1">
                  <FaEdit className="me-1" />
                  Draft mode - Changes auto-saved
                </small>
              )}
            </Col>
            <Col md={6}>
              <div className="d-flex gap-2 justify-content-end">
                <Button
                  variant="outline-secondary"
                  onClick={resetForm}
                  disabled={isSubmitting || isSavingDraft || isSwitchingPatient || isReadOnlyMode}
                  size="sm"
                >
                  <FaUndo className="me-1" />
                  Reset
                </Button>

                <Button
                  variant="outline-success"
                  onClick={saveDraft}
                  disabled={!isDirty || isSubmitting || isSavingDraft || isSwitchingPatient || isReadOnlyMode}
                  size="sm"
                >
                  {isSavingDraft ? (
                    <Spinner animation="border" size="sm" className="me-1" />
                  ) : (
                    <FaSave className="me-1" />
                  )}
                  Save Draft
                </Button>

                <Button
                  variant="primary"
                  onClick={submitForm}
                  disabled={isSubmitting || isSavingDraft || isSwitchingPatient || isReadOnlyMode}
                >
                  {isSubmitting ? (
                    <Spinner animation="border" size="sm" className="me-1" />
                  ) : (
                    <FaPaperPlane className="me-1" />
                  )}
                  Submit
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    );
  };

  const renderPatientDetails = () => (
    <Card className="mb-3">
      <Card.Body className="py-3">
        <Row>
          <Col md={6}>
            <Form.Group className="mb-2">
              <Form.Label className="small">EMR Number</Form.Label>
              <Form.Control
                type="text"
                name="emrNumber"
                value={patientDetails.emrNumber}
                onChange={handlePatientDetailsChange}
                placeholder="Enter EMR Number"
                size="sm"
                readOnly={!!patientDetails.emrNumber && patientDetails.emrNumber.trim() !== ''}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-2">
              <Form.Label className="small">MRN Number</Form.Label>
              <Form.Control
                type="text"
                name="mrnNumber"
                value={patientDetails.mrnNumber}
                onChange={handlePatientDetailsChange}
                placeholder="Enter MRN Number"
                size="sm"
                readOnly={!!patientDetails.mrnNumber && !patientDetails.mrnNumber.match(/^MRN\d{6}$/)}
              />
            </Form.Group>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );

  const renderForm = () => {
    if (!form?.fields) return null;

    return (
      <Card className="shadow-sm">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">{form.name}</h5>
            {currentDraft && (
              <small className="text-muted">
                <FaEdit className="me-1" />
                Draft
              </small>
            )}
          </div>
          {form.description && (
            <small className="text-muted">{form.description}</small>
          )}
        </Card.Header>
        <Card.Body>
          <Row>
            {form.fields.map((field, index) => (
              <Col key={getFieldKey(field, index)} md={field.field_type === 'textarea' || field.field_type === 'header' || field.field_type === 'paragraph' || field.field_type === 'markdown' ? 12 : 6}>
                <FormField
                  field={field}
                  fieldIndex={index}
                  value={getFieldValue(field, index, formValues)}
                  onChange={(value) => handleFieldChange(field, index, value)}
                  error={validationErrors[field.field_id]}
                  disabled={isSubmitting || isSavingDraft || isReadOnlyMode}
                  readOnly={isReadOnlyMode}
                />
              </Col>
            ))}
          </Row>
        </Card.Body>
      </Card>
    );
  };

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  // 🚀 PERFORMANCE OPTIMIZATION: Create single Sidebar instance to prevent duplicate API calls
  const sidebarInstance = <Sidebar key="modern-form-sidebar" />;

  if (loading) {
    return (
      <>
        <Dashboardheader />
        {sidebarInstance}
        <div className="main-content">
          <Container fluid>
            <div className="text-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <p className="mt-3">Loading form...</p>
            </div>
          </Container>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Dashboardheader />
        {sidebarInstance}
        <div className="main-content">
          <Container fluid>
            <Alert variant="danger" className="mt-4">
              <Alert.Heading>Error Loading Form</Alert.Heading>
              <p>{error}</p>
              <Button variant="outline-danger" onClick={() => window.location.reload()}>
                Retry
              </Button>
            </Alert>
          </Container>
        </div>
      </>
    );
  }

  // Create patient object for navigation
  const currentPatientForNavigation = (() => {
    // First try to get from Redux state (for switched patients)
    if (currentPatientId && activePatients.length > 0) {
      const currentPatient = activePatients.find(p => p.id === currentPatientId);
      if (currentPatient) {
        return currentPatient;
      }
    }

    // Fallback to local state (for initial load)
    if (patientId) {
      return {
        id: patientId,
        mrn: patientDetails.mrnNumber || `MRN${patientId}`,
        mrn_number: patientDetails.mrnNumber || `MRN${patientId}`,
        full_name: patientDetails.full_name || 'Loading Patient...',
        status: 'active',
        age: patientDetails.age,
        gender: patientDetails.gender,
        department: patientDetails.department
      };
    }

    // Return a default patient object to prevent null errors
    return {
      id: 'unknown',
      mrn: 'Unknown',
      mrn_number: 'Unknown',
      full_name: 'Unknown Patient',
      status: 'unknown'
    };
  })();

  return (
    <>
      <Dashboardheader />
      {sidebarInstance}

      {/* Enhanced Navigation for Form Submission */}


      <div className="main-content">

        <Container fluid>
          <div className="form-submission-navigation">
            {/* Topbar Navigation */}
            <TopbarNavigation
              folders={formFolders}
              forms={availableForms}
              onFolderSelect={handleTopbarFolderSelect}
              onFormSelect={handleTopbarFormSelect}
              onSearchChange={handleTopbarSearchChange}
              searchTerm={searchTerm}
              selectedFolder={selectedFolder}
              recentForms={recentFormsFromRedux}
              patient={currentPatientForNavigation}
              onPatientChange={handlePatientChange}
            />

            {/* Multi-Patient Tab Bar */}
            {/* <PatientTabBar
          currentPatient={currentPatientForNavigation}
          onPatientChange={handlePatientChange}
          onAddPatient={handleAddPatient}
          maxPatients={5}
        /> */}
          </div>
          {/* Form Actions - Hide for parent in read-only mode */}
          {!(isParentRole && isReadOnlyMode) && renderFormActions()}

          {/* Loading States */}
          {(isLoadingDraft || isLoadingPatientData || isSwitchingPatient) && (
            <Alert variant="info" className="my-3">
              <div className="d-flex align-items-center">
                <Spinner animation="border" size="sm" className="me-2" />
                <span>
                  {isSwitchingPatient && 'Switching patients...'}
                  {isLoadingPatientData && 'Loading patient data...'}
                  {isLoadingDraft && !isLoadingPatientData && !isSwitchingPatient && 'Loading draft...'}
                </span>
              </div>
            </Alert>
          )}

          {/* Patient Load Error */}
          {patientLoadError && (
            <Alert variant="warning" className="my-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>Patient Data Issue:</strong> {patientLoadError}
                </div>
                <Button
                  variant="outline-warning"
                  size="sm"
                  onClick={() => loadPatientData()}
                  disabled={isLoadingPatientData}
                >
                  {isLoadingPatientData ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    'Retry'
                  )}
                </Button>
              </div>
            </Alert>
          )}

          {/* Patient Details - Hide for parent in read-only mode */}
          {!(isParentRole && isReadOnlyMode) && renderPatientDetails()}

          {/* Form Fields - Hide entirely for parent in read-only mode */}
          {!(isParentRole && isReadOnlyMode) && renderForm()}

          {/* Template Manager - Hide for parent role in read-only mode */}
          {formId && !(isParentRole && isReadOnlyMode) && (
            <FormTemplateManager
              formId={formId}
              currentFormData={formValues}
              onTemplateApply={handleTemplateApply}
            />
          )}

          {/* Submission History */}
          {formId && patientId && (
            <FormSubmissionHistory
              key={submissionHistoryKey}
              formId={formId}
              patientId={patientId}
              currentSubmissionId={currentDraft?.submission_id}
              onDraftLoaded={handleDraftLoaded}
              onTemplateLoaded={handleTemplateLoaded}
            />
          )}
        </Container>
      </div>


      {/* Unsaved Changes Indicator */}
      {hasUnsavedChanges && (
        <div className="form-submission-unsaved-indicator">
          <FaEdit className="me-1" />
          Unsaved Changes
        </div>
      )}
    </>
  );
};

export default ModernFormSubmission;